import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';

export const useItinerarySocket = (itineraryId, user) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [presence, setPresence] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Connect to socket
  useEffect(() => {
    if (!itineraryId || !user) return;

    // Create socket connection
    const socket = io(`${SOCKET_URL}/ws`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
      
      // Join the itinerary room
      socket.emit('join_itinerary', {
        itinerary_id: itineraryId,
        user_id: user.id,
        user_name: user.full_name
      });
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    socket.on('connected', (data) => {
      console.log('Connected with SID:', data.sid);
    });

    // Presence updates
    socket.on('presence_update', (data) => {
      setPresence(data.users || []);
    });

    socket.on('user_joined', (data) => {
      setLastUpdate({
        type: 'user_joined',
        user_name: data.user_name,
        timestamp: new Date()
      });
    });

    socket.on('user_left', (data) => {
      setLastUpdate({
        type: 'user_left',
        user_name: data.user_name,
        timestamp: new Date()
      });
    });

    // Item updates
    socket.on('item_added', (data) => {
      setLastUpdate({
        type: 'item_added',
        item: data.item,
        added_by: data.added_by,
        timestamp: new Date()
      });
    });

    socket.on('item_removed', (data) => {
      setLastUpdate({
        type: 'item_removed',
        item_id: data.item_id,
        removed_by: data.removed_by,
        timestamp: new Date()
      });
    });

    socket.on('item_voted', (data) => {
      setLastUpdate({
        type: 'item_voted',
        item_id: data.item_id,
        vote_type: data.vote_type,
        voted_by: data.voted_by,
        timestamp: new Date()
      });
    });

    socket.on('item_commented', (data) => {
      setLastUpdate({
        type: 'item_commented',
        item_id: data.item_id,
        comment: data.comment,
        commented_by: data.commented_by,
        timestamp: new Date()
      });
    });

    socket.on('status_changed', (data) => {
      setLastUpdate({
        type: 'status_changed',
        status: data.status,
        changed_by: data.changed_by,
        timestamp: new Date()
      });
    });

    socket.on('user_typing', (data) => {
      setLastUpdate({
        type: 'user_typing',
        item_id: data.item_id,
        user_name: data.user_name,
        timestamp: new Date()
      });
    });

    // Cleanup on unmount
    return () => {
      if (socket.connected) {
        socket.emit('leave_itinerary', { itinerary_id: itineraryId });
        socket.disconnect();
      }
    };
  }, [itineraryId, user]);

  // Emit functions
  const emitItemAdded = useCallback((item) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('item_added', {
        itinerary_id: itineraryId,
        item
      });
    }
  }, [itineraryId]);

  const emitItemRemoved = useCallback((itemId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('item_removed', {
        itinerary_id: itineraryId,
        item_id: itemId
      });
    }
  }, [itineraryId]);

  const emitItemVoted = useCallback((itemId, voteType) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('item_voted', {
        itinerary_id: itineraryId,
        item_id: itemId,
        vote_type: voteType
      });
    }
  }, [itineraryId]);

  const emitItemCommented = useCallback((itemId, comment) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('item_commented', {
        itinerary_id: itineraryId,
        item_id: itemId,
        comment
      });
    }
  }, [itineraryId]);

  const emitStatusChanged = useCallback((status) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('status_changed', {
        itinerary_id: itineraryId,
        status
      });
    }
  }, [itineraryId]);

  const emitTyping = useCallback((itemId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing', {
        itinerary_id: itineraryId,
        item_id: itemId
      });
    }
  }, [itineraryId]);

  return {
    isConnected,
    presence,
    lastUpdate,
    emitItemAdded,
    emitItemRemoved,
    emitItemVoted,
    emitItemCommented,
    emitStatusChanged,
    emitTyping
  };
};

export default useItinerarySocket;
